import { useState } from 'react';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { TextBlock } from '../../Atoms/Text';
import { DashboardWidgets } from '../../Compounds/DashboardWidgets/DashboardWidgets';
import { Icon, IconType } from '../../Atoms/Icon';
import { DatePicker } from '../../Atoms/DatePicker';
import { SelectMenu } from '../../Atoms/SelectMenu';
import { ProgressBar } from '../../Atoms/ProgressBar';
import { Toggle } from '../../Atoms/Toggle';
import { Tooltip } from '../../Atoms/Tooltip';
import { mockInventoryData } from '../Inventory/mockInventoryData';
import { DocumentPreviewTool } from '../../Atoms/DocumentPreviewTool';
import { DisableCustomizationsForm } from '../../Compounds/DisableCustomizationsForm';
import { getDefaultPreferences } from '../../../store/User';
import { JSONSchemaForm, JSONSchemaFormProps } from '../../Compounds/JSONSchemaForm';
import { outsideInventoryWidgets } from '../../Compounds/OutsideInventoryForm/outsideInventoryWidgets';
import testSchema from '../../Compounds/OutsideInventoryForm/OutsideInventoryTestSchema.json';
import { RJSFSchema } from '@rjsf/utils';
import { Tags } from '../../Compounds/Tagging';
import { mockProjectData } from '../Projects/mockProjectData';
import { SupportedTagEntityTypes } from '../../../store/Tags';

const defaultJSONSchemaFormProps: JSONSchemaFormProps<Record<string, string>> = {
  formKey: '',
  formData: {},
  schema: testSchema.schema.validation_schema as RJSFSchema,
  uiSchema: testSchema.schema.ui_schema,
  rjsfProps: {
    widgets: outsideInventoryWidgets
  }
};

export const StyleGuide = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const [terms, setTerms] = useState<number>(30);
  const [sun, setSun] = useState<boolean>(true);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);

  const page = 'Style Guide';
  const iconTypes: IconType[] = [
    'orders',
    'inventory',
    'support-cases',
    'triangle',
    'checked',
    'dropdown',
    'customize',
    'sort',
    'filter',
    'dots',
    'calendar',
    'details',
    'more',
    'collapse',
    'expand',
    'plus',
    'plusRectangle',
    'minus',
    'homeIcon',
    'ordersIcon',
    'inventoryIcon',
    'casesIcon',
    'logoutIcon',
    'greyCheckmark',
    'greenCheckmark',
    'indigoCheckmark',
    'alert',
    'cancel',
    'retry',
    'trash',
    'myTeam',
    'pin',
    'redCancel',
    'impersonate',
    'key',
    'settings',
    'contact',
    'logoutSmall',
    'refresh',
    'newDoc',
    'external',
    'info',
    'edit',
    'disabled',
    'enabled'
  ];

  const handleDateChange = (selectedDate: Date) => {
    setDate(selectedDate);
  };

  const termsOptions = [
    { value: '30', text: 'Month' },
    { value: '90', text: 'Quarter' },
    { value: '365', text: 'Year' }
  ];

  const handleToggleChange = (oldSunValue: boolean) => {
    setSun(!oldSunValue);
  };

  const toggleModal = () => {
    setIsShowModal(!isShowModal);
  };

  return (
    <PageLayout pageTitle={page}>
      <PageCard title="Icons">
        <div className="flex flex-wrap">
          {iconTypes.map((iconType) => {
            return (
              <div
                className="basis-1/5 h-[66px] flex items-center"
                key={iconType}>
                <Icon
                  type={iconType}
                  className="p-[10px] m-[10px] h-[20px] w-[20px]"
                />
                <TextBlock className="font-medium">{iconType}</TextBlock>
              </div>
            );
          })}
        </div>
      </PageCard>
      <PageCard title="Atoms">
        <div className="flex flex-wrap">
          <div className="basis-1/4 flex items-center justify-center">
            <DatePicker
              date={date}
              handleDateChange={handleDateChange}
            />
          </div>
          <div className="basis-1/12 flex items-center">
            <TextBlock className="font-medium">DatePicker</TextBlock>
          </div>
          <div className="basis-1/4 flex items-center">
            <ProgressBar
              progress={66}
              className="w-full rounded"
            />
          </div>
          <div className="basis-1/12 flex items-center justify-center">
            <TextBlock className="font-medium">ProgressBar</TextBlock>
          </div>
          <div className="basis-1/4 flex items-center">
            <SelectMenu
              handleSelect={(e) => setTerms(Number(e.target.value))}
              name="terms"
              options={termsOptions}
            />
            {terms}
          </div>
          <div className="basis-1/12 flex items-center justify-center">
            <TextBlock className="font-medium">SelectMenu</TextBlock>
          </div>
          <div className="basis-1/4 flex items-center justify-center">
            <Toggle
              className=""
              checked={sun}
              onChange={() => handleToggleChange(sun)}
            />
          </div>
          <div className="basis-1/12 flex items-center">
            <TextBlock className="font-medium">Toggle</TextBlock>
          </div>
          <div className="basis-1/4 flex items-center justify-center">
            <Tooltip
              show
              value="Lorem Ipsum"
            />
          </div>
          <div className="basis-1/12 flex items-center justify-center">
            <TextBlock className="font-medium">Tooltip</TextBlock>
          </div>
          <div className="basis-1/4 flex items-center justify-center">
            <DocumentPreviewTool
              documents={[{ id: 'id', uri: '/UPSTACK-Logo.png', fileName: 'Upstack Logo', fileType: 'png' }]}
              fileType="png"
              styles={{ height: '150px', width: '150px' }}
            />
          </div>
          <div className="basis-1/12 flex items-center justify-center">
            <TextBlock className="font-medium">Document Preview Tool</TextBlock>
          </div>
        </div>
      </PageCard>
      <PageCard title="Modal">
        <button onClick={() => toggleModal()}>Toggle Modal</button>
        <div />
      </PageCard>
      <PageCard title="Widgets">
        <DashboardWidgets
          inventoryAssets={mockInventoryData}
          pageTitle={page}
          cases={[]}
          projects={[]}
        />
      </PageCard>
      <PageCard title="Enable Columns Form">
        <div className="flex flex-row">
          <DisableCustomizationsForm
            type="column"
            header="Table Customizations"
            subheader="Enable and disable table columns for the users you're creating."
            gridPrefs={getDefaultPreferences().content.inventory_grid_All}
            className="flex flex-row justify-center h-[80vh] w-[49%] overflow-scroll"
            saveHandler={() => null}
          />
          <DisableCustomizationsForm
            type="widget"
            header="Widget Customizations"
            subheader="Enable and disable widgets for the users you're creating."
            widgets={getDefaultPreferences().content.widgets}
            className="flex flex-row justify-center h-[80vh] w-[49%] overflow-scroll"
            saveHandler={() => null}
          />
        </div>
      </PageCard>
      <PageCard title="JSON Schema Form">
        <div className="flex flex-row justify-start">
          <div className="w-[80%]">
            <JSONSchemaForm {...defaultJSONSchemaFormProps} />
          </div>
        </div>
      </PageCard>

      <PageCard title="Tag Component">
        <div className="flex flex-row justify-start">
          <div className="w-[80%]">
            <Tags
              entity={mockProjectData[0]}
              entityId={mockProjectData[0].id}
              entityType={SupportedTagEntityTypes.PROJECT}
              entityFetchFn={async (id: string) => {
                // Simulate fetching the project data
                console.log(`Fetching project with id: ${id}`);
              }}
            />
          </div>
        </div>
      </PageCard>
    </PageLayout>
  );
};
